import { app } from '@app/core/initialize-services/InitializeServices';
import { Stop, Tour, Border } from '@app/shared/models/tour.type';
import axios from 'axios';
import {
	connectFirestoreEmulator,
	enableIndexedDbPersistence,
	getFirestore,
	enableMultiTabIndexedDbPersistence,
	doc,
	getDoc,
	getDocFromServer,
	getDocFromCache,
} from 'firebase/firestore';

export async function isIncognito(): Promise<boolean> {
	return new Promise((resolve) => {
		const fs = (window as any).RequestFileSystem || (window as any).webkitRequestFileSystem;
		if (!fs) {
			// Firefox or Safari — fallback
			const db = indexedDB.open('test');
			db.onerror = () => resolve(true);
			db.onsuccess = () => resolve(false);
		} else {
			fs((window as any).TEMPORARY, 100, () => resolve(false), () => resolve(true));
		}
	});
}

export const getInitializedFirestore = (app: any) => {
	const firestore = getFirestore(app);
	if (process.env.NODE_ENV === 'development') {
		return firestore;
	}
	// only enable emulator if in dev mode, and we haven't already enabled it
	if (
		process.env.NODE_ENV === 'development' &&
		(firestore as any)._getSettings().host === 'firestore.googleapis.com'
	) {
		connectFirestoreEmulator(getFirestore(app), '127.0.0.1', 5173);
	}

	// only enable indexeddb if the firestore client hasn't been fully initialized
	if (!(firestore as any)._firestoreClient) {
		enableMultiTabIndexedDbPersistence(firestore)
			.then(() => console.log('Persistence enabled!'))
			.catch((err) => console.error(err.message));
	}
	return firestore;
};

export const service_tourService = {
	saveUserAddedStop: async (uID: string, tourID: string, stopObject: any): Promise<any> => {
		const url = `https://added-stops-613221148836.europe-west1.run.app`;
		try {
			const response = await fetch(url, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					uID,
					tourID,
					stopID: stopObject.place_id || null, // Pass ID if it exists (for updates)
					...stopObject
				})
			});
			if (!response.ok) {
				console.warn('[tourService] Failed to save user stop:', response.status);
				return null;
			}
			const result = await response.json();
			// Return the stop with ID from server (important for new stops)
			return { ...stopObject, place_id: result.id || stopObject.place_id };
		} catch (error) {
			console.error('[tourService] Error saving user stop:', error);
			return null;
		}
	},

	deleteUserAddedStop: async (uID: string, tourID: string, stopID: string): Promise<boolean> => {
		const url = `https://added-stops-613221148836.europe-west1.run.app`;
		try {
			const response = await fetch(url, {
				method: 'DELETE',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					uID,
					tourID,
					stopID
				})
			});
			if (!response.ok) {
				console.warn('[tourService] Failed to delete user stop:', response.status);
				return false;
			}
			return true;
		} catch (error) {
			console.error('[tourService] Error deleting user stop:', error);
			return false;
		}
	},

	getUserAddedStops: async (uID: string, tourID: string): Promise<any[]> => {
		const url = `https://added-stops-613221148836.europe-west1.run.app?uID=${encodeURIComponent(uID)}&tourID=${encodeURIComponent(tourID)}`;
		try {
			const response = await fetch(url, {
				method: 'GET',
				headers: { 'Content-Type': 'application/json' }
			});
			if (!response.ok) {
				console.warn('[tourService] Failed to fetch user stops:', response.status);
				return [];
			}
			const stops = await response.json();
			return Array.isArray(stops) ? stops : [];
		} catch (error) {
			console.error('[tourService] Error fetching user stops:', error);
			return [];
		}
	},

	getSavedStops: async (uID: string, tourID: string): Promise<string[]> => {
		const url = `https://added-stops-613221148836.europe-west1.run.app?uID=${encodeURIComponent(uID)}&tourID=${encodeURIComponent(tourID)}&category=savedStops`;
		try {
			const response = await fetch(url, {
				method: 'GET',
				headers: { 'Content-Type': 'application/json' }
			});
			if (!response.ok) {
				console.warn('[tourService] Failed to fetch saved stops:', response.status);
				return [];
			}
			const savedStops = await response.json();
			// Extract stop IDs from the response (assuming response is array of objects with id/stopID)
			if (Array.isArray(savedStops)) {
				return savedStops.map((stop: any) => stop.stopID || stop.id || stop);
			}
			return [];
		} catch (error) {
			console.error('[tourService] Error fetching saved stops:', error);
			return [];
		}
	},

	saveSavedStop: async (uID: string, tourID: string, stopID: string): Promise<boolean> => {
		const url = 'https://added-stops-613221148836.europe-west1.run.app';
		try {
			const response = await fetch(url, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					uID,
					tourID,
					category: 'savedStops',
					stopID,
				}),
			});
			if (!response.ok) {
				console.warn('[tourService] Failed to save saved stop:', response.status);
				return false;
			}
			return true;
		} catch (error) {
			console.error('[tourService] Error saving saved stop:', error);
			return false;
		}
	},

	deleteSavedStop: async (uID: string, tourID: string, stopID: string): Promise<boolean> => {
		const url = `https://added-stops-613221148836.europe-west1.run.app?uID=${encodeURIComponent(uID)}&tourID=${encodeURIComponent(tourID)}&category=savedStops&stopID=${encodeURIComponent(stopID)}`;
		try {
			const response = await fetch(url, {
				method: 'DELETE',
			});
			if (!response.ok) {
				console.warn('[tourService] Failed to delete saved stop:', response.status);
				return false;
			}
			return true;
		} catch (error) {
			console.error('[tourService] Error deleting saved stop:', error);
			return false;
		}
	},

	getTourByIdNew: async (toudId: string) => {
		const url = `https://europe-central2-tours-app-1579553856346.cloudfunctions.net/getTour?tourId=${toudId}`;
		const response = await axios.get(url);
		const tour = response?.data?.data
		tour.stops = tour.stops.map((stop: Stop) => {
			(stop as any).tags = stop.tags?.map((tag: { text: string, icon: string }) => !tag?.text ? { text: tag, icon: '' } : tag);
			return stop;
		});

		return tour;
	},

	getBorders: async (tourId: string): Promise<Border[] | null> => {
		try {
			const url = `https://europe-central2-tours-app-1579553856346.cloudfunctions.net/getTour?tourId=${tourId}&includeBoarders=true`;
			const response = await axios.get(url);
			const borders = response?.data?.data?.border; // Structure: response.data.data.border
			return borders || null;
		} catch (error) {
			console.warn('[tourService] Failed to fetch borders:', error);
			return null;
		}
	},

	getNotificationsForTour: async (tourId: string, uID: string): Promise<any[]> => {
		const url = `https://get-notifications-for-tour-613221148836.europe-west1.run.app?tourId=${encodeURIComponent(tourId)}&uID=${encodeURIComponent(uID)}`;
		try {
			const response = await fetch(url);
			if (!response.ok) {
				console.warn('[tourService] Failed to fetch notifications:', response.status);
				return [];
			}
			const data = await response.json();
			const notifications = Array.isArray(data) ? data : (data?.notifications || []);
			return notifications;
		} catch (error) {
			console.error('[tourService] Error fetching notifications:', error);
			return [];
		}
	},

	updateNotificationState: async (
		tourId: string,
		notificationId: string,
		uID: string,
		action: 'seen' | 'dismissed' | 'ctaClicked' | 'ctaSkipped' | 'seenOnList'
	): Promise<boolean> => {
		const url = 'https://update-notification-state-613221148836.europe-west1.run.app';
		try {
			const response = await fetch(url, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					tourId,
					notificationId,
					uID,
					action,
					timestamp: Date.now(),
				}),
			});
			if (!response.ok) {
				console.warn('[tourService] Failed to update notification state:', response.status);
				return false;
			}
			return true;
		} catch (error) {
			console.error('[tourService] Error updating notification state:', error);
			return false;
		}
	},

	batchUpdateNotificationState: async (
		tourId: string,
		uID: string,
		updates: Array<{
			notificationId: string;
			action: 'seen' | 'dismissed' | 'ctaClicked' | 'ctaSkipped' | 'seenOnList';
			timestamp: number;
		}>
	): Promise<{ success: boolean; results?: Record<string, { action: string; success: boolean }> }> => {
		const url = 'https://batch-update-notification-state-613221148836.europe-west1.run.app';
		try {
			const response = await fetch(url, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					tourId,
					uID,
					updates,
				}),
			});
			if (!response.ok) {
				console.warn('[tourService] Failed to batch update notification states:', response.status);
				return { success: false };
			}
			const data = await response.json();
			return data;
		} catch (error) {
			console.error('[tourService] Error batch updating notification states:', error);
			return { success: false };
		}
	},

	getUserTours: async (uID: string, gID?: string): Promise<any> => {
		const url = 'https://get-all-tours-613221148836.europe-west1.run.app';
		const body: any = { userIdentifier: uID };
		if (gID) body.guideId = gID;

		try {
			const response = await fetch(url, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(body),
			});
			if (!response.ok) {
				console.warn('[tourService] Failed to fetch user tours:', response.status);
				return [];
			}
			const data = await response.json();
			console.log('Fetched user tours:', data);
			// Response structure: { success: true, data: { tours: [...], foundUserId, meta: {...} } }
			// Return full data object so component can access tours, foundUserId, meta
			return data?.data || { tours: data?.tours || [] };
		} catch (error) {
			console.error('[tourService] Error fetching user tours:', error);
			return [];
		}
	},
};
